import { createSelector } from 'reselect';
import get from 'lodash/get';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';
import compact from 'lodash/compact';
import camelCase from 'lodash/camelCase';

import { IDS } from '../../constants/ids';
import {
  APPOINTMENT_INSPECTION,
  EIR_APPOINTMENT_TYPES,
  TELEMEDICINE_VISIT_TYPE_CODE,
} from '../../constants';

import { makeSelectorFactory } from '../../utils/redux/selectAdapter';

const { OTHER_REASON_TYPE } = IDS.APPOINTMENT_FORM;

export const selectRootState = state => get(state, 'appointmentForm');
const getSelectors = makeSelectorFactory(selectRootState);

/**
 *  PROGRESS
 * */
const selectProgressState = createSelector(
  selectRootState,
  state => get(state, 'progress', {}),
);

export const selectPharmaciesList = createSelector(
  selectRootState,
  state => get(state, 'pharmaciesList', null),
);

export const selectMedicationSearchData = createSelector(
  selectRootState,
  state => get(state, 'medicationsList.list', []),
);

export const selectMedicationSearch = createSelector(
  selectRootState,
  state => get(state, 'medicationsList', {}),
);

export const selectPreferredPharmacy = createSelector(
  selectProgressState,
  progress => get(progress, 'pharmacyId'),
);

export const selectProgressValidating = createSelector(
  selectProgressState,
  progress => get(progress, 'validating'),
);
export const selectProgressCareUser = createSelector(
  selectProgressState,
  progress => get(progress, 'careUser'),
);
export const selectProgressCareUserId = createSelector(
  selectProgressState,
  progress => get(progress, 'careUser.id'),
);

export const selectProgressCareUserDisplayableName = createSelector(
  selectProgressState,
  progress => get(progress, 'careUser.displayableName', ''),
);

export const selectProgressCareUserSkipInsurance = createSelector(
  selectProgressState,
  progress => get(progress, 'careUser.skipInsurance', false),
);

export const selectProgressCareUserHasPrimaryCareProvider = createSelector(
  selectProgressState,
  progress => get(progress, 'careUser.hasPrimaryCareProvider', false),
);

export const selectProgressOrganization = createSelector(
  selectProgressState,
  progress => get(progress, 'organization', {}),
);
export const selectProgressOrganizationId = createSelector(
  selectProgressOrganization,
  progress => get(progress, 'id', null),
);

export const selectProgressOrganizationTenantSkipInsurance = createSelector(
  selectProgressOrganization,
  progress => get(progress, 'tenant.skipInsurance', false),
);

export const selectProgressVisitType = createSelector(
  selectProgressState,
  progress => get(progress, 'visitType', {}),
);
export const selectProgressVisitTypeCode = createSelector(
  selectProgressVisitType,
  progress => get(progress, 'code', ''),
);
export const selectProgramBasedVisitId = createSelector(
  selectProgressState,
  progress => get(progress, 'pbvConfigId'),
);
export const selectProgressServicesByVisit = createSelector(
  selectProgressVisitType,
  visitType => get(visitType, 'services', []),
);
export const selectProgressLocationsByVisit = createSelector(
  selectProgressServicesByVisit,
  services => get(services[0], 'locations', []),
);
export const selectProgressService = createSelector(
  selectProgressState,
  progress => get(progress, 'service', {}),
);
export const selectProgressLocation = createSelector(
  selectProgressState,
  progress => get(progress, 'location', {}),
);
export const selectProgressPatientForm = createSelector(
  selectProgressState,
  progress => get(progress, 'patientForm', null),
);
export const selectHealthInfoForm = createSelector(
  selectProgressState,
  progress => get(progress, 'healthInfo', null),
);
export const selectProgressSSN = createSelector(
  selectProgressState,
  progress => get(progress, 'ssn', ''),
);
export const selectProgressReason = createSelector(
  selectProgressState,
  progress => get(progress, 'reason', null),
);
export const selectProgressError = createSelector(
  selectProgressState,
  progress => get(progress, 'error', null),
);
export const selectProgressSchedule = createSelector(
  selectProgressState,
  progress => get(progress, 'schedule', {}),
);
export const selectProgressRescheduleData = createSelector(
  selectProgressState,
  progress => get(progress, 'rescheduleData', {}),
);
export const selectProgressIsRescheduleData = createSelector(
  selectProgressState,
  progress => get(progress, 'rescheduleData.id', null),
);
export const selectProgressScheduleId = createSelector(
  selectRootState,
  progress => get(progress, 'providerGWDetails.data.id', ''),
);
export const selectPrimaryProviderData = createSelector(
  selectRootState,
  progress => get(progress, 'primaryProviderData.data', null),
);
export const selectProgressScheduleProvider = createSelector(
  selectProgressSchedule,
  schedule => get(schedule, 'provider', {}),
);

export const selectProgressIsCreateFlowGW = createSelector(
  selectProgressState,
  data => get(data, 'isGWFlow', false),
);

export const selectProgressIsUrgentVisitFlow = createSelector(
  selectProgressState,
  data => get(data, 'isUrgentVisitFlow', false),
);

export const selectProgressScheduleProviderOrganizationName = createSelector(
  selectProgressSchedule,
  schedule => get(schedule, 'provider.organization.name', ''),
);

export const selectProgressInsuranceEligibilityId = createSelector(
  selectProgressState,
  data => get(data, 'insuranceEligibility.id', null),
);

export const selectProgressLocationIdWithUVVFlow = createSelector(
  selectProgressService,
  service => get(service, 'locations[0].id'),
);

export const selectProgressLocationIdWithLocalVisit = createSelector(
  selectProgressLocation,
  location => get(location, 'id'),
);

export const selectProgressScheduleLocationId = createSelector(
  selectProgressLocationIdWithUVVFlow,
  selectProgressIsUrgentVisitFlow,
  selectProgressSchedule,
  selectProgressLocationIdWithLocalVisit,
  (locationIdUVV, isUrgentVisitFlow, schedule, locationIdByLocalVisit) => {
    if (isUrgentVisitFlow) {
      return locationIdUVV;
    }
    if (locationIdByLocalVisit) {
      return locationIdByLocalVisit;
    }
    return get(schedule, 'location.id');
  },
);

export const selectIsEirVisitTypeCode = createSelector(
  selectProgressVisitTypeCode,
  typeCode => EIR_APPOINTMENT_TYPES.includes(typeCode) || false,
);

export const selectIsEirTelemedicineVisitTypeCode = createSelector(
  selectProgressVisitTypeCode,
  typeCode => typeCode === TELEMEDICINE_VISIT_TYPE_CODE,
);

export const selectAppointmentFormPending = createSelector(
  selectProgressState,
  progress => get(progress, 'pending'),
);

export const selectFlowPages = createSelector(
  selectProgressState,
  progress => get(progress, 'flowPages'),
);

/**
 *  MY PROVIDERS
 * */

const selectMyProvidersState = createSelector(
  selectRootState,
  state => get(state, 'myProviders', {}),
);

export const selectMyProvidersLoading = createSelector(
  selectMyProvidersState,
  state => get(state, 'sending', false),
);

export const selectMyProvidersCount = createSelector(
  selectMyProvidersState,
  state => get(state, 'count', 0),
);

export const selectMyProvidersList = createSelector(
  selectMyProvidersState,
  state => get(state, 'data.appointments', []),
);

export const selectMyProvidersVisitTypes = createSelector(
  selectMyProvidersState,
  state => get(state, 'data.visitTypes', []),
);

export const selectMyProvidersPaginationLoading = createSelector(
  selectMyProvidersState,
  state => get(state, 'paginationSending', false),
);

/**
 *  AVAILABLE PROVIDERS SELF SCHEDULING
 * */

export const selectAvailableProvidersSelfScheduling = createSelector(
  selectRootState,
  data => get(data, 'availableProvidersSelfScheduling.data', []),
);

export const selectAvailableProvidersSelfSchedulingLoading = createSelector(
  selectRootState,
  data => get(data, 'availableProvidersSelfScheduling.sending', false),
);

export const selectUrgentAppointmentId = createSelector(
  selectRootState,
  data => get(data, 'confirmVisitUrgent.id', null),
);

/**
 *  AVAILABLE EIR LOCATIONS
 * */

export const selectAvailableEirLocationsData = createSelector(
  selectRootState,
  data => get(data, 'availableEirLocations.data', []),
);

export const selectAvailableEirLocationLoading = createSelector(
  selectRootState,
  data => get(data, 'availableEirLocations.sending', false),
);

/**
 *  SCHEDULING TREE
 * */

export const selectSchedulingTree = createSelector(
  selectRootState,
  data => get(data, 'schedulingTree', {}),
);

export const selectSchedulingTreeOrganizations = createSelector(
  selectSchedulingTree,
  data => get(data, 'organizations', []),
);

/**
 *  CARE USERS LIST
 * */
export const {
  selectStateList: selectCareUsersList,
  selectStatePending: selectCareUsersPending,
} = getSelectors('careUsers');

/**
 *  ORGANIZATION LIST
 * */
export const {
  selectStateList: selectOrganizationList,
  selectStatePending: selectOrganizationPending,
} = getSelectors('organizations');

/**
 *  VISIT TYPE LIST
 * */
export const {
  selectStatePending: selectVisitTypesPending,
  selectStateList: selectVisitTypesList,
} = getSelectors('visitTypes');

/**
 *  PUBLIX INFO
 * */
export const selectPublixInfo = createSelector(
  selectRootState,
  state => get(state, 'publixInfo.guidePages', []),
);
export const { selectStatePending: selectPublixInfoPending } = getSelectors(
  'publixInfo',
);

/**
 *  INLINE VISITS TIME SLOTS
 * */
export const {
  selectStateData: selectInlineVisitTimeSlots,
  selectStatePending: selectInlineVisitTimeSlotsPending,
} = getSelectors('inlineVisitTimeSlots');

/**
 *  CONFITM INLINE VISIT
 * */
export const {
  selectStateSucceed: selectSuccessConfirmInlineVisit,
} = getSelectors('confirmInlineVisit');

/**
 *  PROGRAM BASED VISITS LIST
 * */
export const {
  selectStateList: selectProgramBasedVisitsList,
  selectStatePending: selectProgramBasedVisitsPending,
} = getSelectors('programBasedVisits');

/**
 *  INSURANCE ELIGIBILITY PAYERS LIST
 * */
export const {
  selectState: selectInsurancePayersState,
  selectStateList: selectInsurancePayersList,
  selectStatePending: selectInsurancePayersLoading,
} = getSelectors('insurancePayers');

export const selectInsurancePayersMetaMessage = createSelector(
  selectInsurancePayersState,
  state => get(state, 'meta.message', ''),
);

export const selectInsurancePayersIsLastPage = createSelector(
  selectInsurancePayersState,
  state => get(state, 'isLastPage', false),
);

export const selectInsurancePayersFetchingSucceed = createSelector(
  selectInsurancePayersState,
  state => get(state, 'succeed', null),
);

/**
 *  LATEST CHECK INSURANCE
 * */
export const {
  selectState: selectInsuranceLatestCheckData,
  selectStatePending: selectInsuranceLatestCheckLoading,
  selectStateError: selectInsuranceLatestCheckError,
} = getSelectors('latestCheckInsuranceEligibility');

export const selectInsuranceLatestCheck = createSelector(
  selectRootState,
  state => get(state, 'latestCheckInsuranceEligibility.userInputs', {}),
);

/**
 *  CHECK INSURANCE ELIGIBILITY
 * */
export const {
  selectStateError: selectCheckInsuranceEligibilityError,
  selectStatePending: selectCheckInsuranceEligibilityLoading,
  selectStateSucceed: selectCheckInsuranceEligibilitySucceed,
} = getSelectors('checkInsuranceEligibility');

export const selectInsuranceEligibilityId = createSelector(
  selectRootState,
  state => get(state, 'checkInsuranceEligibility.id'),
);

/**
 *  VIRTUAL VISIT SERVICES LIST
 * */

export const selectVirtualVisitServicesList = createSelector(
  selectVisitTypesList,
  state => {
    const visit = state.find(item => item.code === 'W');
    return get(visit, 'services', []);
  },
);

export const selectVirtualVisitLocations = createSelector(
  selectVirtualVisitServicesList,
  list => {
    const locations = list.reduce(
      (prev, curr) => [...prev, ...curr.locations],
      [],
    );
    return uniqBy(locations, 'id');
  },
);

export const selectVirtualServiceByLocationId = id =>
  createSelector(
    selectVirtualVisitServicesList,
    list => {
      return list.filter(service => {
        const locations = get(service, 'locations', []);
        return locations.some(item => item.id === id);
      });
    },
  );

/**
 *  INSPECTOR
 * */
const selectInspectorState = createSelector(
  selectRootState,
  state => get(state, 'inspection', {}),
);
export const selectInspectorPending = createSelector(
  selectInspectorState,
  state => get(state, 'sending', false),
);
export const selectInspectorRequiredSteps = createSelector(
  selectInspectorState,
  state => get(state, 'requiredSteps', []),
);
export const selectInspectorIds = createSelector(
  selectInspectorState,
  state => get(state, 'ids', []),
);
export const selectInspectorEntities = createSelector(
  selectInspectorState,
  state => get(state, 'entities', {}),
);
export const selectInspectionList = createSelector(
  selectInspectorIds,
  selectInspectorEntities,
  (ids, entities) => ids.map(id => entities[id]),
);
export const makeSelectInspectionById = id =>
  createSelector(
    selectInspectorEntities,
    entities => entities[id],
  );

export const selectProfileFormInspectionFieldListErrors = createSelector(
  selectInspectorEntities,
  entity => get(entity, `${APPOINTMENT_INSPECTION.PROFILE}.fieldList`, []),
);

export const selectProfileFormInspectionFieldListErrorsKeys = createSelector(
  selectProfileFormInspectionFieldListErrors,
  list => list.map(item => camelCase(get(item, 'key'))),
);

/**
 *  INSPECTION
 * */
export const { selectStatePending: selectInspectConsentPending } = getSelectors(
  'inspectConsent',
);
export const {
  selectStatePending: selectInspectPatientPending,
  selectStateList: selectInspectPatientList,
} = getSelectors('inspectPatient');
export const {
  selectStatePending: selectInspectDocumentsPending,
} = getSelectors('inspectDocuments');

/**
 *  PATIENT INSPECTION ERRORS
 * */

export const selectPatientFieldsListErrors = createSelector(
  selectInspectPatientList,
  list => {
    return get(list[0], 'fieldList', []);
  },
);

/**
 *  CONSENT FORM
 * */
export const {
  selectStatePending: selectConsentFormPending,
  selectStateData: selectConsentFormData,
  makeSelect: makeConsentFormSelect,
} = getSelectors('consent');

export const selectConsentFormUrl = makeConsentFormSelect('data.file', '');

/**
 *  USER DETAILS
 * */
export const {
  selectStatePending: selectUserDetailsPending,
  selectStateData: selectUserDetails,
  makeSelect: makeUserSelect,
} = getSelectors('userDetails');

export const selectSSN = makeUserSelect('data.ssn');

export const selectUserCountryDetails = makeUserSelect('data.country');

export const selectUserEmergencyContactAttributes = createSelector(
  selectRootState,
  state =>
    get(
      state,
      'userDetails.data.primaryPatientProfile.emergencyContactAttributes',
      {},
    ),
);

export const selectUserPrimaryPatientProfile = createSelector(
  selectRootState,
  state => get(state, 'userDetails.data.primaryPatientProfile', {}),
);

export const selectUserPrimaryPatientProfileGuarantorId = createSelector(
  selectRootState,
  state =>
    get(state, 'userDetails.data.primaryPatientProfile.guarantorUser.id', null),
);

export const selectUserPrimaryPatientProfileId = createSelector(
  selectRootState,
  state => get(state, 'userDetails.data.primaryPatientProfile.id', null),
);

export const selectAppointmentFormUserCountry = createSelector(
  selectRootState,
  state => get(state, 'userDetails.data.country', ''),
);

export const selectAppointmentFormUserState = createSelector(
  selectRootState,
  state => get(state, 'userDetails.data.state', ''),
);

export const selectEirVisitTypePCPFormData = createSelector(
  selectRootState,
  state => get(state, 'progress.pcpFormData'),
);

export const selectAdditionalInfoFormData = createSelector(
  selectRootState,
  state => get(state, 'progress.additionalInfoFormData'),
);

/**
 *  REASON TYPE LIST
 * */
export const {
  selectStatePending: selectReasonTypesPending,
  selectStateList: selectReasonTypesList,
} = getSelectors('reasonTypes');

/**
 *  REASONS VISIT LIST
 * */
export const {
  selectStateList: selectReasonVisitList,
  selectStatePending: selectReasonVisitPending,
} = getSelectors('reasonVisit');

/**
 *  UPDATE NECESSARY PROFILE
 * */
export const {
  selectStatePending: selectUpdateNecessaryDataPending,
} = getSelectors('necessaryData');

export const selectProgressGlobalPending = createSelector(
  selectInspectorPending,
  selectInspectConsentPending,
  selectInspectPatientPending,
  selectInspectDocumentsPending,
  selectUpdateNecessaryDataPending,
  selectAppointmentFormPending,
  (...pendingList) => pendingList.some(pending => Boolean(pending)),
);

/**
 *  APP FILTERS
 * */
const selectFiltersState = createSelector(
  selectRootState,
  state => get(state, 'filters', {}),
);
export const selectAppointmentDateFilter = createSelector(
  selectFiltersState,
  state => get(state, 'date'),
);
export const selectAppointmentLocationFilter = createSelector(
  selectFiltersState,
  state => get(state, 'location'),
);

/**
 *  FILTER LIST
 * */
const selectFilterListState = createSelector(
  selectRootState,
  state => get(state, 'filterList', {}),
);
export const selectAppointmentFilterPending = createSelector(
  selectFilterListState,
  state => get(state, 'sending', false),
);
export const selectAppointmentFilterList = createSelector(
  selectFilterListState,
  state => get(state, 'list', []),
);

/**
 *  CHECK INSURANCE PLAN CONFIG
 * */
export const selectCheckInsurancePlanConfigState = createSelector(
  selectRootState,
  state => get(state, 'checkInsurancePlanConfig', {}),
);

export const selectCheckInsurancePlanConfigLoading = createSelector(
  selectRootState,
  state => get(state, 'loading', false),
);

/**
 *  SCHEDULE LIST PROVIDERS
 * */
export const selectAppointmentProviderListState = createSelector(
  selectRootState,
  state => get(state, 'schedulings', {}),
);

export const selectAppointmentProviderListPending = createSelector(
  selectAppointmentProviderListState,
  state => get(state, 'sending', false),
);

export const selectAppointmentProviderListIds = createSelector(
  selectAppointmentProviderListState,
  state => get(state, 'ids', []),
);

const selectAppointmentProviderListEntities = createSelector(
  selectAppointmentProviderListState,
  state => get(state, 'entities', {}),
);

export const selectAppointmentProviderListMeta = createSelector(
  selectAppointmentProviderListState,
  state => get(state, 'meta', {}),
);

export const selectAppointmentProviderList = createSelector(
  selectAppointmentProviderListIds,
  selectAppointmentProviderListEntities,
  (ids, entities) => ids.map(id => entities[id]),
);

export const makeAppointmentProviderByIdSelect = (id, locationId) =>
  createSelector(
    selectAvailableProvidersSelfScheduling,
    locations => {
      const targetLocation = locations.find(
        location => locationId === location.id.toString(),
      );
      return targetLocation
        ? targetLocation.providers.find(
            provider => provider.id.toString() === id,
          ) || {}
        : {};
    },
  );

/**
 *  PROVIDER GW DETAILS
 * */

export const {
  selectStatePending: selectProviderGWDetailsPending,
  selectStateData: selectProviderGWDetails,
  makeSelect: makeProviderGWDetailsSelect,
} = getSelectors('providerGWDetails');

export const selectCurrentProviderGWAvailableTimeSlots = makeProviderGWDetailsSelect(
  'data.availableProviders[0].timeSlots',
  [],
);

/**
 *  PROVIDER DETAILS
 * */

export const {
  selectStatePending: selectProviderDetailsPending,
  selectStateData: selectProviderDetails,
  makeSelect: makeProviderDetailsSelect,
} = getSelectors('providerDetails');

export const selectCurrentProviderDetails = makeProviderDetailsSelect(
  'data[0].providers[0]',
);
export const selectCurrentProviderAvailableTimeSlots = makeProviderDetailsSelect(
  'data[0].providers[0].timeSlots',
  [],
);

export const selectScheduleMetaDataFromProviderList = createSelector(
  selectAppointmentProviderListMeta,
  meta => pick(meta, ['id', 'forDate']),
);
export const selectScheduleMetaDataFromProviderInfo = createSelector(
  selectProviderGWDetails,
  data => pick(data, ['id', 'forDate']),
);

/**
 *  SCHEDULE PAYLOAD GATHERING
 * */
export const selectSchedulePayloadData = createSelector(
  selectProgressCareUser,
  selectProgressService,
  selectProgressReason,
  (user, service, reason) => ({
    userId: user.id,
    payload: {
      serviceId: get(service, 'id'),
      reasonTypeId: get(reason, 'reasonType.id'),
      reasonNote: get(reason, 'reasonNote'),
    },
  }),
);

export const selectScheduleListReqPayload = createSelector(
  selectSchedulePayloadData,
  selectAppointmentDateFilter,
  selectAppointmentLocationFilter,
  (payload, date, filterIds) => ({
    ...payload,
    date,
    filterIds,
  }),
);

export const selectFilterPayloadData = createSelector(
  selectProgressService,
  selectProgressVisitType,
  selectProgressOrganizationId,
  (service, visitType, organizationId) => ({
    serviceId: get(service, 'id'),
    visitTypeId: get(visitType, 'id'),
    organizationId,
  }),
);

/**
 *  APPOINTMENT OVERVIEW
 * */
export const selectReasonForOverview = createSelector(
  [selectProgressReason, selectProgressService],
  (reason, service) => {
    const additionInformation = reason.isHomeLocationDifferent
      ? {
          state: get(reason, 'state'),
          country: get(reason, 'country'),
          isHomeLocationDifferent: get(reason, 'isHomeLocationDifferent'),
        }
      : {};

    if (service.reasonTypes) {
      const result =
        service.reasonTypes.find(item => item.id === reason.reasonType.id) ||
        {};

      return {
        id: result.id,
        note: reason.reasonNote,
        type: result.name,
        ...additionInformation,
      };
    }

    return {
      id: get(reason, 'reasonType.id'),
      note: reason.reasonNote,
      type: get(reason, 'reasonType.name'),
      ...additionInformation,
    };
  },
);

export const selectCopayPrice = createSelector(
  selectRootState,
  state => get(state, 'fetchCopay.copay', undefined),
);

/**
 *  CONFIRM VISIT GW
 * */
export const { selectStatePending: selectConfirmVisitGWPending } = getSelectors(
  'confirmVisitGW',
);

/**
 *  CONFIRM VISIT
 * */
export const { selectStatePending: selectConfirmVisitPending } = getSelectors(
  'confirmVisit',
);

/**
 *  VIRTUAL VISIT PROVIDERS
 * */

export const {
  selectStatePending: selectVirtualVisitProvidersPending,
  selectStateList: selectVirtualVisitProvidersList,
} = getSelectors('virtualVisitProviders');

/**
 *  CONFIRM SELFT SCHEDULINGS
 * */
export const {
  selectStatePending: selectSelfSchedulingConfirmPending,
} = getSelectors('confirmVisitSelfScheduling');

/**
 *  CONFIRM URGENT VISIT
 * */
export const {
  selectStatePending: selectUrgentVisitConfirmPending,
} = getSelectors('confirmVisitUrgent');

export const selectUrgentVisitPayload = createSelector(
  selectProgressCareUserId,
  selectProgressOrganizationId,
  selectProgressLocationIdWithUVVFlow,
  selectProgressInsuranceEligibilityId,
  selectProgressVisitType,
  selectProgressService,
  selectProgressReason,
  selectPreferredPharmacy,
  selectHealthInfoForm,
  (
    userId,
    organizationId,
    locationId,
    insuranceEligibilityResultId,
    visitType,
    service,
    reason,
    pharmacyId,
    healthInfo,
  ) => {
    const reasonNote = get(reason, 'reasonVisit', [])
      .filter(({ value }) => value !== OTHER_REASON_TYPE)
      .map(({ label }) => label)
      .concat(get(reason, 'reasonNote'));

    const state = get(reason, 'state', null);
    const country = get(reason, 'country', null);

    return {
      id: userId,
      ...(state && { state }),
      ...(country && { country }),
      locationId,
      pharmacyId,
      organizationId,
      insuranceEligibilityResultId,
      serviceId: get(service, 'id'),
      visitTypeId: get(visitType, 'id'),
      reasonTypeId: get(reason, 'reasonType.id'),
      reasonNote: compact(reasonNote).join(', '),
      ...omit(healthInfo, ['patientProfile']),
      ...healthInfo.patientProfile,
      userAppPlatform: 'web',
    };
  },
);

/**
 *  CREATE VIRTUAL VISIT
 * */

export const {
  selectStatePending: selectCreateVirtualVisitPending,
  selectStateError: selectCreateVirtualVisitError,
} = getSelectors('createVirtualVisit');

export const selectOverlapModalState = createSelector(
  selectRootState,
  data => get(data, 'overlapModal.opened'),
);

export const selectOverlapModalEntity = createSelector(
  selectRootState,
  data => get(data, 'overlapModal.entity'),
);

export const selectInsuranceErrorModalState = createSelector(
  selectRootState,
  data => get(data, 'insuranceErrorModal.opened'),
);

export const selectInsuranceErrorModalEntity = createSelector(
  selectRootState,
  data => get(data, 'insuranceErrorModal.entity'),
);

export const selectPcpProviderAnswer = createSelector(
  selectHealthInfoForm,
  state => get(state, 'pcpProviderAnswer', ''),
);

/**
 *  CREATE EIR URGENT CARE APPOINTMENT
 * */

export const selectCreateEirUrgentCareAppointmentPending = createSelector(
  selectRootState,
  state => get(state, 'createEirUrgentCareAppointment.sending'),
);

/**
 *  PREFERRED LANGUAGE
 * */

export const selectPreferredLanguage = createSelector(
  selectProgressState,
  state => get(state, 'language'),
);

/**
 *  PRIMARY CARE PROVIDERS LIST
 * */

export const selectOrganizationPrimaryCareProvidersListState = createSelector(
  selectRootState,
  state => get(state, 'primaryCareProvidersList'),
);

export const selectOrganizationPrimaryCareProvidersList = createSelector(
  selectOrganizationPrimaryCareProvidersListState,
  state => get(state, 'list', []),
);

export const selectOrganizationPrimaryCareProvidersListLoading = createSelector(
  selectOrganizationPrimaryCareProvidersListState,
  state => get(state, 'sending', false),
);

export const selectFetchingOrganizationPrimaryCareProvidersListSucceed = createSelector(
  selectOrganizationPrimaryCareProvidersListState,
  state => get(state, 'succeed'),
);

export const selectIsOrganizationPrimaryCareProvidersLastPage = createSelector(
  selectOrganizationPrimaryCareProvidersListState,
  state => get(state, 'isLastPage', false),
);

/**
 *  EMPLOYERS LIST
 * */

export const selectOrganizationEmployersListState = createSelector(
  selectRootState,
  state => get(state, 'employersList'),
);

export const selectOrganizationEmployersList = createSelector(
  selectOrganizationEmployersListState,
  state => get(state, 'list', []),
);

export const selectOrganizationEmployersListLoading = createSelector(
  selectOrganizationEmployersListState,
  state => get(state, 'sending', false),
);

export const selectFetchingOrganizationEmployersListSucceed = createSelector(
  selectOrganizationEmployersListState,
  state => get(state, 'succeed'),
);

export const selectIsOrganizationEmployersLastPage = createSelector(
  selectOrganizationEmployersListState,
  state => get(state, 'isLastPage', false),
);

/**
 *  APPOINTMENT PAYMENT INFO MODAL
 * */

export const selectAppointmentPaymentInfoModal = createSelector(
  selectRootState,
  state => get(state, 'appointmentPaymentInfoModal', {}),
);

export const selectAppointmentPaymentInfoModalOpened = createSelector(
  selectAppointmentPaymentInfoModal,
  state => get(state, 'opened'),
);

export const selectAppointmentPaymentInfoModalProps = createSelector(
  selectAppointmentPaymentInfoModal,
  state => get(state, 'props'),
);



// WEBPACK FOOTER //
// ./src/redux/appointmentForm/selectors.js